import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { PreferencesDialog } from '../components/ui/PreferencesDialog'
import { useAuth } from './AuthProvider'

interface PreferencesDialogContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
}

const PreferencesDialogContext = createContext<PreferencesDialogContextValue | null>(null)

export function PreferencesDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const { user } = useAuth()

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])

  // Auto-close on sign-out / session expiry. Without this the dialog
  // can sit on top of the login screen — the router re-renders to
  // <LoginPage>, but our `isOpen` state survives the auth flip. This
  // happens routinely when the same Supabase user is shared across
  // apps (e.g. Datacore signs the user out mid-session in HappyTask).
  useEffect(() => {
    if (!user) setIsOpen(false)
  }, [user])

  return (
    <PreferencesDialogContext.Provider value={{ isOpen, open, close }}>
      {children}
      <PreferencesDialog open={isOpen} onOpenChange={setIsOpen} />
    </PreferencesDialogContext.Provider>
  )
}

export function usePreferencesDialog() {
  return useContext(PreferencesDialogContext)
}
